/**
 * Cut a value out of a JSON document without reserializing it.
 *
 * The async and batch routes wrap the extract response in an envelope, so the
 * thing the certificate endpoint has to receive is a *member* of the body
 * that arrived. Getting it by `JSON.stringify(parsed.result)` looks
 * equivalent and is not:
 *
 *     Python emits   {"perimeter_ft": 1758.0}
 *     JS re-emits    {"perimeter_ft": 1758}
 *
 * JavaScript has one number type and prints a whole-valued double without its
 * fractional part. The server then parses that back as an integer, canonicalizes
 * it as `1758`, and computes a different HMAC from the one it signed. The
 * certificate is refused with "the payload was modified after extraction",
 * which is true: it was, by us, in transit.
 *
 * So the raw substring is taken instead. This is a scanner rather than a
 * regex because JSON strings can contain braces and escaped quotes, and a
 * regex that gets that wrong fails on exactly the deeds whose verbatim text
 * quotes a monument.
 */

/** The exact source text of `object[key]`, or null when it is not there. */
export function sliceMember(raw: string, key: string): string | null {
  const needle = `"${key}"`;
  let at = -1;
  // Find the key at the top level of the outermost object, so a nested
  // "result" inside the extraction cannot be mistaken for the envelope's.
  let depth = 0;
  let index = 0;
  while (index < raw.length) {
    const char = raw[index];
    if (char === '"') {
      const end = endOfString(raw, index);
      if (depth === 1 && raw.slice(index, end + 1) === needle) {
        at = end + 1;
        break;
      }
      index = end + 1;
      continue;
    }
    if (char === "{" || char === "[") depth += 1;
    else if (char === "}" || char === "]") depth -= 1;
    index += 1;
  }
  if (at < 0) return null;

  while (at < raw.length && /\s/.test(raw[at])) at += 1;
  if (raw[at] !== ":") return null;
  at += 1;
  while (at < raw.length && /\s/.test(raw[at])) at += 1;
  const end = endOfValue(raw, at);
  return end < 0 ? null : raw.slice(at, end);
}

/** The index of the closing quote of the string starting at `start`. */
function endOfString(raw: string, start: number): number {
  let index = start + 1;
  while (index < raw.length) {
    const char = raw[index];
    if (char === "\\") {
      index += 2;
      continue;
    }
    if (char === '"') return index;
    index += 1;
  }
  return raw.length - 1;
}

/** One past the last character of the JSON value starting at `start`. */
function endOfValue(raw: string, start: number): number {
  const first = raw[start];
  if (first === '"') return endOfString(raw, start) + 1;
  if (first === "{" || first === "[") {
    let depth = 0;
    let index = start;
    while (index < raw.length) {
      const char = raw[index];
      if (char === '"') {
        index = endOfString(raw, index) + 1;
        continue;
      }
      if (char === "{" || char === "[") depth += 1;
      else if (char === "}" || char === "]") {
        depth -= 1;
        if (depth === 0) return index + 1;
      }
      index += 1;
    }
    return -1;
  }
  // A literal: number, true, false, null. It ends at the next structural
  // character.
  let index = start;
  while (index < raw.length && !",}]".includes(raw[index])) index += 1;
  return index;
}

/** The nth element of a top-level array member, as source text. */
export function sliceArrayElements(raw: string): string[] {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("[")) return [];
  const elements: string[] = [];
  let index = 1;
  for (;;) {
    while (index < trimmed.length && /[\s,]/.test(trimmed[index])) index += 1;
    if (index >= trimmed.length || trimmed[index] === "]") break;
    const end = endOfValue(trimmed, index);
    if (end < 0) break;
    elements.push(trimmed.slice(index, end));
    index = end;
  }
  return elements;
}
